import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Typography,
} from "@mui/joy";

interface Props {
  platforms: Array<{
    id?: number;
    platform: string;
    name: string;
    accountName?: string;
  }>;
  title: string;
  content: string;
  organizationName?: string;
  organizationLogo?: string;
  images?: string[];
  scheduledTime?: Date;
  platformContent?: Record<string, any>;
}
export const SocialMediaPreview: React.FC<Props> = ({
  platforms,
  title,
  content,
  organizationName = "Your business",
  organizationLogo,
  images = [],
  scheduledTime,
  platformContent = {},
}) => {
  const [imageIndex, setImageIndex] = useState(0);
  return (
    <Stack spacing={2}>
      <Alert variant="soft">
        Approximate previews using your selected accounts and media. Final
        cropping and layout are controlled by each platform.
      </Alert>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2,minmax(0,1fr))",
            lg: "repeat(3,minmax(0,1fr))",
          },
          gap: 2,
        }}
      >
        {platforms.map((account) => {
          const name = account.accountName || organizationName;
          const media =
            account.platform === "google_business_profile"
              ? images.slice(0, 1)
              : images;
          const index = Math.min(imageIndex, Math.max(0, media.length - 1));
          const variation = platformContent[account.platform] || {};
          const caption = variation.message ?? content;
          return (
            <Stack
              key={account.id ?? account.platform}
              spacing={1}
              sx={{ minWidth: 0 }}
            >
              <Typography level="title-sm">{account.name} preview</Typography>
              <Card variant="outlined" sx={{ minWidth: 0, overflow: "hidden" }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Avatar src={organizationLogo}>{name[0]}</Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      level="title-sm"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {name}
                    </Typography>
                    <Typography level="body-xs">
                      {scheduledTime && !Number.isNaN(scheduledTime.getTime())
                        ? scheduledTime.toLocaleString()
                        : "Draft"}
                    </Typography>
                  </Box>
                </Box>
                {account.platform === "google_business_profile" && (
                  <Typography level="title-md">{title}</Typography>
                )}
                {account.platform !== "instagram" && (
                  <Typography
                    sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                  >
                    {caption}
                    {variation.hashtags?.length
                      ? `\n\n${variation.hashtags.join(" ")}`
                      : ""}
                  </Typography>
                )}
                {media.length > 0 ? (
                  <Box
                    component="img"
                    src={media[index]}
                    alt={`${name} post image ${index + 1}`}
                    sx={{
                      width: "100%",
                      aspectRatio:
                        account.platform === "instagram" ? "1" : "4/3",
                      objectFit: "cover",
                      borderRadius: "sm",
                    }}
                  />
                ) : (
                  account.platform === "instagram" && (
                    <Alert color="warning">
                      Select an image before scheduling an Instagram post.
                    </Alert>
                  )
                )}
                {media.length > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      size="sm"
                      variant="plain"
                      disabled={index === 0}
                      onClick={() => setImageIndex(index - 1)}
                    >
                      Previous image
                    </Button>
                    <Chip size="sm">
                      {index + 1}/{media.length}
                    </Chip>
                    <Button
                      size="sm"
                      variant="plain"
                      disabled={index === media.length - 1}
                      onClick={() => setImageIndex(index + 1)}
                    >
                      Next image
                    </Button>
                  </Box>
                )}
                {account.platform === "instagram" && (
                  <Typography
                    sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                  >
                    {caption}
                    {variation.hashtags?.length
                      ? `\n\n${variation.hashtags.join(" ")}`
                      : ""}
                  </Typography>
                )}
                {account.platform === "google_business_profile" &&
                  images.length > 1 && (
                    <Typography level="body-xs">
                      Google Business Profile publishes the first image.
                    </Typography>
                  )}
              </Card>
            </Stack>
          );
        })}
      </Box>
    </Stack>
  );
};
